import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://carriera-backend.onrender.com/api';

// JTK column headers (Bahasa Malaysia), in sheet order. J/K excluded per JTK.
const HEADERS = [
  'Bil', 'Tarikh Daftar', 'Nama Pencari Kerja', 'Alamat Pencari Kerja', 'No. K/P',
  'Jantina', 'Nama Majikan', 'Alamat Majikan', 'No SSM', 'Pekerjaan',
  'Upah/Gaji', 'Fi Yang Dipungut'
];

const MALAY_MONTHS = [
  'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
  'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'
];

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function rowCells(r, i) {
  return [
    i + 1,
    fmtDate(r.tarikh_daftar),
    r.nama_pencari_kerja,
    r.alamat_pencari_kerja,
    r.no_kp,
    r.jantina,
    r.nama_majikan,
    r.alamat_majikan,
    r.no_ssm,
    r.pekerjaan,
    r.upah_gaji === '' || r.upah_gaji == null ? '' : `RM${r.upah_gaji}`,
    r.fi_dipungut === '' || r.fi_dipungut == null ? '' : `RM${r.fi_dipungut}`
  ];
}

// 'YYYY-MM' helpers
function ym(y, m) { return `${y}-${String(m).padStart(2, '0')}`; }

export default function JtkReport() {
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth() + 1;
  const [from, setFrom] = useState(ym(thisYear, 1));
  const [to, setTo] = useState(ym(thisYear, thisMonth));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/jtk?from=${from}&to=${to}`, { headers });
      setReport(data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  async function downloadExcel() {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/reports/jtk.xlsx?from=${from}&to=${to}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `JTK-Penempatan-${from}-hingga-${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Gagal memuat turun fail Excel.');
    } finally {
      setDownloading(false);
    }
  }

  const years = [];
  for (let y = thisYear + 1; y >= 2023; y--) years.push(y);

  function MonthYear({ value, onChange, label }) {
    const [y, m] = value.split('-').map(Number);
    return (
      <div>
        <label className="block text-xs text-carriera-muted mb-1">{label}</label>
        <div className="flex gap-2">
          <select value={m} onChange={e => onChange(ym(y, Number(e.target.value)))}
            className="border border-carriera-border px-2 py-2 text-sm focus:outline-none focus:border-carriera-accent">
            {MALAY_MONTHS.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
          </select>
          <select value={y} onChange={e => onChange(ym(Number(e.target.value), m))}
            className="border border-carriera-border px-2 py-2 text-sm focus:outline-none focus:border-carriera-accent">
            {years.map(yy => <option key={yy} value={yy}>{yy}</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Reports</p>
        <h2 className="font-serif text-3xl font-normal text-carriera-text">JTK Annual Report</h2>
        <p className="text-sm text-carriera-muted mt-1">
          Rekod Pendaftaran Pencari Kerja, Penempatan dan Fi Dipungut (Seksyen 19(1), Akta Agensi Pekerjaan Swasta 1981).
          Follows the invoice: every placement the client has paid for, including candidates who later dropped out. Free replacements and refunded placements are not listed.
        </p>
      </div>

      <div className="flex items-end gap-3 mb-6 flex-wrap">
        <MonthYear value={from} onChange={setFrom} label="Dari (from)" />
        <span className="self-end pb-2 text-carriera-muted text-sm">→</span>
        <MonthYear value={to} onChange={setTo} label="Hingga (to)" />
        <button onClick={downloadExcel} disabled={downloading || !report?.total}
          className="text-sm bg-carriera-text text-white px-4 py-2 hover:bg-carriera-accent transition-colors disabled:opacity-40">
          {downloading ? 'Preparing...' : 'Download Excel'}
        </button>
        <p className="self-end pb-2 text-xs text-carriera-muted">
          {report ? `${report.total} placement${report.total === 1 ? '' : 's'}` : ''}
        </p>
      </div>

      {loading && <p className="text-sm text-carriera-muted">Loading...</p>}

      {!loading && report && report.months.length === 0 && (
        <p className="text-sm text-carriera-muted">Tiada penempatan untuk tempoh ini.</p>
      )}

      {!loading && report && report.months.map(({ key, label, rows }) => (
        <div key={key} className="mb-8">
          <p className="font-serif text-lg text-carriera-text font-medium underline mb-2">{label}</p>
          <div className="overflow-x-auto border border-carriera-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-carriera-text text-white">
                  {HEADERS.map(h => (
                    <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-carriera-border odd:bg-white even:bg-carriera-bg">
                    {rowCells(r, i).map((cell, j) => (
                      <td key={j} className="px-2 py-1.5 text-carriera-sec align-top">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
